<script>
// 마이 페이지 — 회원 정보 / 비밀번호 변경 / SMS 설정
//   레거시 DlgPwSettings.vue 의 show() / handleOk() / pw_change() / sms_update() 패턴
//   - 정보 탭: 변경된 필드만 update_member 호출
//   - 비밀번호 탭: new + renew 입력 시에만 reset_pw_member 호출
//   - SMS 탭: sms_active 가 변경된 경우만 set_sms_setting 호출 (level < 2 일 때만 노출)
import { getMember, updateMember, resetPwMember, setSmsSetting } from '@/api/users'

const TYPE_OPTIONS = [
  { value: 0, key: 'type_wiplat' },
  { value: 1, key: 'type_client' },
  { value: 2, key: 'type_outsourcing' },
]
const AUTH_OPTIONS = [
  { value: 0, key: 'auth_admin' },
  { value: 1, key: 'auth_manager' },
  { value: 2, key: 'auth_user' },
  { value: 3, key: 'auth_guest' },
]
const STATUS_OPTIONS = [
  { value: 'A', key: 'status_active' },
  { value: 'I', key: 'status_inactive' },
  { value: 'U', key: 'status_unable' },
]
const YN_OPTIONS = [
  { value: 'Y', key: 'yn_yes' },
  { value: 'N', key: 'yn_no' },
]

export default {
  name: 'MyPageModal',
  props: {
    open: { type: Boolean, default: false },
    // 어드민이 다른 사용자 정보를 열람/수정할 때 사용 — 빈 값이면 본인 정보 (sessionStorage.m_id)
    targetMId: { type: String, default: '' },
  },
  emits: ['close'],
  data() {
    return {
      activeTab: 'props',
      properties: null,
      propertiesBak: null,
      mId: '',
      cId: '',
      mIdx: '',
      newPw: '',
      renewPw: '',
      smsYn: 'N',
      smsYnBak: 'N',
      smsCheck: false,
      loading: false,
      saving: false,
      error: '',
    }
  },
  computed: {
    typeOptions()    { return TYPE_OPTIONS.map(o => ({ value: o.value, label: this.$t(`mypage_info.${o.key}`) })) },
    authOptions()    { return AUTH_OPTIONS.map(o => ({ value: o.value, label: this.$t(`mypage_info.${o.key}`) })) },
    statusOptions()  { return STATUS_OPTIONS.map(o => ({ value: o.value, label: this.$t(`mypage_info.${o.key}`) })) },
    ynOptions()      { return YN_OPTIONS.map(o => ({ value: o.value, label: this.$t(`mypage_info.${o.key}`) })) },
    showSmsTab() {
      // 레거시: sessionStorage.level < 2 일 때만 SMS 설정 가능
      const lvl = Number(sessionStorage.level)
      return Number.isFinite(lvl) && lvl < 2
    },
  },
  watch: {
    open(v) {
      if (v) this.show()
    },
  },
  methods: {
    async show() {
      this.activeTab = 'props'
      this.properties    = null
      this.propertiesBak = null
      this.newPw    = ''
      this.renewPw  = ''
      this.error    = ''
      // targetMId 가 주어지면 그 사용자 정보를 로드, 아니면 현재 로그인 사용자 본인
      const m_id = this.targetMId || sessionStorage.m_id || ''
      this.mId = m_id
      if (!m_id) return
      this.loading = true
      try {
        const res = await getMember(m_id)
        if (res?.check === 0 && res?.info) {
          this.properties    = { ...res.info }
          this.propertiesBak = { ...res.info }
          this.cId  = res.info.c_id
          this.mIdx = res.info.id
          this.smsYn    = res.info.sms_yn ?? 'N'
          this.smsYnBak = res.info.sms_yn ?? 'N'
          this.smsCheck = !this.showSmsTab
        } else {
          this.error = this.$t('mypage_info.alert_load_fail')
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[MyPageModal] load fail', e)
        this.error = this.$t('mypage_info.alert_load_fail')
      } finally {
        this.loading = false
      }
    },
    onBackdrop(e) {
      if (e.target === e.currentTarget) this.$emit('close')
    },
    async handleOk() {
      if (!this.properties || !this.propertiesBak) return
      this.saving = true
      try {
        // (1) 비밀번호 변경 — new 가 입력된 경우만
        if (this.newPw) {
          if (!this.checkPasswordRule(this.mId, this.newPw)) return
          if (this.newPw.length < 4) {
            alert(this.$t('mypage_info.alert_pw_too_short'))
            return
          }
          if (this.newPw !== this.renewPw) {
            alert(this.$t('mypage_info.alert_pw_mismatch'))
            return
          }
          try {
            const res = await resetPwMember(this.mIdx, this.newPw)
            if (res?.check === 0) {
              alert(this.$t('mypage_info.alert_pw_changed'))
            } else if (res?.msg) {
              alert(res.msg)
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('[MyPageModal] pw change fail', e)
          }
        }

        // (2) SMS 설정 — 변경된 경우만
        if (this.showSmsTab && this.smsYn !== this.smsYnBak) {
          try {
            const res = await setSmsSetting({
              sms_active: this.smsYn,
              customer_id: this.cId,
              customer_member_id: this.mIdx,
              mobile: this.properties.mobile_phone_number,
              user_name: this.properties.name,
            })
            if (res?.check === 0) {
              alert(this.$t('mypage_info.alert_sms_changed'))
              this.smsYnBak = this.smsYn
            } else if (res?.msg) {
              alert(res.msg)
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('[MyPageModal] sms update fail', e)
          }
        }

        // (3) 회원 정보 수정 — 변경된 필드만
        const fields = {}
        Object.keys(this.propertiesBak).forEach(k => {
          if (this.propertiesBak[k] !== this.properties[k]) fields[k] = this.properties[k]
        })
        if (Object.keys(fields).length > 0) {
          try {
            await updateMember(this.properties.id, fields)
            alert(this.$t('mypage_info.alert_member_updated'))
            this.propertiesBak = { ...this.properties }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('[MyPageModal] update_member fail', e)
            alert(this.$t('mypage_info.alert_save_fail'))
          }
        }

        this.$emit('close')
      } finally {
        this.saving = false
      }
    },
    // 레거시 fnCheckPassword — 동일 문자 4회 이상 사용 금지만 유지 (그 외 룰은 주석처리되어 있었음)
    checkPasswordRule(_uid, upw) {
      if (/(\w)\1\1\1/.test(upw)) {
        alert(this.$t('mypage_info.alert_pw_repeat'))
        return false
      }
      return true
    },
  },
}
</script>

<template>
  <Teleport to="body">
    <transition name="fade">
      <div v-if="open" class="mp-backdrop" @click="onBackdrop">
        <div class="mp-modal" role="dialog" aria-modal="true">
          <div class="mp-header">
            <h3>{{ $t('mypage_info.title') }}</h3>
            <button class="mp-close" :title="$t('mypage_info.btn_cancel')" @click="$emit('close')">&times;</button>
          </div>

          <div class="mp-tabs">
            <button :class="['mp-tab', activeTab === 'props' ? 'on' : '']" @click="activeTab = 'props'">
              {{ $t('mypage_info.tab_props') }}
            </button>
            <button :class="['mp-tab', activeTab === 'pw' ? 'on' : '']" @click="activeTab = 'pw'">
              {{ $t('mypage_info.tab_pw') }}
            </button>
            <button
              v-if="showSmsTab"
              :class="['mp-tab', activeTab === 'sms' ? 'on' : '']"
              @click="activeTab = 'sms'"
            >{{ $t('mypage_info.tab_sms') }}</button>
          </div>

          <div class="mp-body">
            <div v-if="loading" class="mp-msg">{{ $t('common.loading') || '...' }}</div>
            <div v-else-if="error" class="mp-msg err">{{ error }}</div>

            <template v-else-if="properties">
              <!-- 정보 탭 -->
              <div v-show="activeTab === 'props'" class="mp-form">
                <label>{{ $t('mypage_info.id') }}</label>             <input v-model="properties.id" disabled/>
                <label>{{ $t('mypage_info.email') }}</label>          <input v-model="properties.email" disabled/>
                <label>{{ $t('mypage_info.name') }}</label>           <input v-model="properties.name"/>
                <label>{{ $t('mypage_info.department') }}</label>     <input v-model="properties.department" disabled/>
                <label>{{ $t('mypage_info.mobile_phone_number') }}</label> <input v-model="properties.mobile_phone_number"/>
                <label>{{ $t('mypage_info.type') }}</label>
                <select v-model="properties.type" disabled>
                  <option v-for="o in typeOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
                </select>
                <label>{{ $t('mypage_info.authority_type') }}</label>
                <select v-model="properties.authority_type" disabled>
                  <option v-for="o in authOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
                </select>
                <label>{{ $t('mypage_info.register_datetime') }}</label> <input v-model="properties.register_datetime" disabled/>
                <label>{{ $t('mypage_info.status') }}</label>
                <select v-model="properties.status">
                  <option v-for="o in statusOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
                </select>
              </div>

              <!-- PW Change 탭 -->
              <div v-show="activeTab === 'pw'" class="mp-form">
                <label>{{ $t('mypage_info.label_id') }}</label>     <input v-model="mId" disabled/>
                <label>{{ $t('mypage_info.label_new_pw') }}</label> <input v-model="newPw" type="password" :placeholder="$t('mypage_info.ph_new_pw')"/>
                <label>{{ $t('mypage_info.label_renew_pw') }}</label> <input v-model="renewPw" type="password" :placeholder="$t('mypage_info.ph_renew_pw')"/>
              </div>

              <!-- SMS 탭 -->
              <div v-show="activeTab === 'sms' && showSmsTab" class="mp-form">
                <label>{{ $t('mypage_info.id') }}</label>          <input v-model="properties.id" disabled/>
                <label>{{ $t('mypage_info.label_id') }}</label>    <input v-model="mId" disabled/>
                <label>{{ $t('mypage_info.label_customer') }}</label> <input v-model="properties.c_name" disabled/>
                <label>{{ $t('mypage_info.status') }}</label>
                <select v-model="smsYn">
                  <option v-for="o in ynOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
                </select>
              </div>
            </template>
          </div>

          <div class="mp-footer">
            <button class="mp-cancel" :disabled="saving" @click="$emit('close')">{{ $t('mypage_info.btn_cancel') }}</button>
            <button class="mp-ok" :disabled="loading || saving || !properties" @click="handleOk">
              {{ saving ? $t('mypage_info.btn_saving') : $t('mypage_info.btn_ok') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.mp-backdrop {
  position: fixed; inset: 0;
  background: rgba(5,10,20,.65);
  backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center;
  z-index: 10000;
  font-family: Pretendard, sans-serif;
}
.mp-modal {
  width: min(540px, 92vw);
  max-height: 86vh;
  background: var(--bg-base);
  border: 1px solid var(--border-subtle);
  border-radius: 0.625rem;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 40px rgba(0,0,0,.5);
  overflow: hidden;
}
.mp-header {
  display: flex; align-items: center;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid var(--border-subtle);
}
.mp-header h3 {
  flex: 1; margin: 0;
  font-size: 1rem; font-weight: 700; color: var(--text-primary); letter-spacing: .02em;
}
.mp-close {
  width: 1.75rem; height: 1.75rem;
  background: transparent;
  border: 1px solid var(--border-subtle);
  border-radius: 0.375rem;
  color: #7a90b0;
  font-size: 1rem;
  cursor: pointer;
  transition: color .15s, background .15s;
}
.mp-close:hover { color: var(--status-err); border-color: rgba(var(--status-err-rgb),.4); background: rgba(var(--status-err-rgb),.08); }

.mp-tabs {
  display: flex;
  border-bottom: 1px solid var(--border-subtle);
  background: var(--bg-surface);
}
.mp-tab {
  flex: 1;
  padding: 8px 12px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--text-disabled);
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: .04em;
  cursor: pointer;
  transition: color .15s, border-color .15s;
}
.mp-tab:hover { color: var(--text-muted); }
.mp-tab.on   { color: var(--accent); border-bottom-color: var(--accent); }

.mp-body {
  flex: 1;
  overflow: auto;
  padding: 14px 16px;
  background: var(--bg-base);
}
.mp-msg {
  display: flex; align-items: center; justify-content: center;
  min-height: 120px;
  color: var(--text-disabled); font-size: 0.8125rem;
}
.mp-msg.err { color: var(--status-err); }

.mp-form {
  display: grid;
  grid-template-columns: 9rem 1fr;
  gap: 8px 12px;
  align-items: center;
}
.mp-form label {
  font-size: 0.8125rem;
  color: var(--text-disabled);
  font-weight: 600;
  text-align: right;
}
.mp-form input,
.mp-form select {
  padding: 5px 8px;
  font-size: 0.8125rem;
  background: var(--bg-surface-2);
  color: var(--text-primary);
  border: 1px solid var(--border-subtle);
  border-radius: 0.25rem;
  outline: none;
}
.mp-form input:focus,
.mp-form select:focus { border-color: rgba(var(--accent-rgb),.4); }
.mp-form input:disabled,
.mp-form select:disabled { color: var(--text-disabled); cursor: default; }

.mp-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 14px;
  background: var(--bg-surface);
  border-top: 1px solid var(--border-subtle);
}
.mp-cancel,
.mp-ok {
  padding: 5px 14px;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: background .15s, color .15s, border-color .15s;
}
.mp-cancel { background: transparent; color: var(--text-muted); border: 1px solid var(--border-subtle); }
.mp-cancel:hover:not(:disabled) { color: var(--text-secondary); border-color: #2a4570; }
.mp-ok {
  background: rgba(var(--accent-rgb),.15);
  color: var(--accent);
  border: 1px solid var(--accent);
}
.mp-ok:hover:not(:disabled) { background: rgba(var(--accent-rgb),.25); }
.mp-cancel:disabled,
.mp-ok:disabled { opacity: .5; cursor: not-allowed; }

.fade-enter-active, .fade-leave-active { transition: opacity .18s; }
.fade-enter-from, .fade-leave-to       { opacity: 0; }
</style>
